import type {
  ConfidenceEnvelope,
  OverviewExpenseCategory,
  OverviewToday,
  OverviewVariation,
  OwnerOverview,
} from '../types/dashboard';

/**
 * Owner Intelligence v1 — lógica de presentación PURA del bloque "Así va tu negocio" (spec §8, §9,
 * §13, §14, §18). Sin React, testeable en node — mismo patrón que `homeSummary.ts`.
 *
 * Aquí se deciden los OCHO estados del bloque y el microcopy. La regla que gobierna todo:
 * nunca "$0.00" donde el número es DESCONOCIDO. Un cero real se dice; un cero fabricado, no.
 */

export type OverviewBlockState =
  | 'LOADING'
  | 'ERROR'
  | 'NO_ACCESS'      // MANAGER sin acceso financiero: el bloque explica por qué
  | 'READY';

export interface OverviewBlockView {
  state: OverviewBlockState;
  /** Frase única cuando el bloque no puede mostrar números. */
  message: string | null;
  /** Etiqueta del alcance ("Todas las tiendas", "Centro y Norte — …"). */
  scopeLabel: string;
  /**
   * Lo que estas cifras NO incluyen, dicho en una línea. Vacío cuando no hay nada que advertir.
   * Cubre dos casos que antes se callaban:
   *   · M-3 — con alcance acotado, los gastos y compras generales del negocio quedan fuera;
   *   · H-2 — con tiendas en zonas horarias distintas, cada una aporta su propio día local.
   */
  scopeNotes: string[];
}

/** Las advertencias de alcance que el bloque debe declarar (M-3, H-2). */
export function scopeNotes(overview: OwnerOverview): string[] {
  const notes: string[] = [];
  if (overview.scope.excludesBusinessWideRecords) {
    notes.push('Solo tus tiendas: los gastos y compras generales del negocio no están incluidos.');
  }
  if (overview.mixedTimezones) {
    notes.push('Tus tiendas están en horarios distintos. Cada una cuenta su propio día.');
  }
  return notes;
}

/**
 * El signo va ANTES del símbolo de moneda: "−$97,600.00", no "$-97,600.00". Un "Te quedó" negativo
 * es una lectura importante del bloque y tiene que leerse de un vistazo.
 */
const money = (value: string | number) => {
  const amount = Number(value);
  const magnitude = Math.abs(amount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${amount < 0 ? '−' : ''}$${magnitude}`;
};

/** Importe con signo explícito, para las comparaciones ("+$4,300", "−$2,600"). */
export function signedMoney(value: string | number): string {
  const amount = Number(value);
  if (amount === 0) return money(0);
  return `${amount > 0 ? '+' : '−'}${money(Math.abs(amount))}`;
}

export function formatMoney(value: string | number): string {
  return money(value);
}

/**
 * NUEVO-3 · lo que se puede decir de una categoría de gasto, según si hay con qué compararla.
 *
 * Sin periodo anterior comparable no se dice "apareció este periodo" ni se pinta una variación:
 * lo único cierto es que no hay historia. "No puedo comparar" y "no existía" son afirmaciones
 * distintas, y confundirlas convertía en descubrimiento a una categoría de toda la vida.
 */
export function expenseChangeLabel(row: OverviewExpenseCategory, comparable: boolean): string {
  if (!comparable) return 'sin historia para comparar';
  if (row.isNew) return 'apareció este periodo';
  const change = Number(row.changeAmount);
  if (change === 0) return 'igual que antes';
  return `${change > 0 ? '▲' : '▼'} ${money(Math.abs(change))}`;
}

/** El color del renglón. Sin comparación no hay dirección que colorear. */
export function expenseChangeTone(row: OverviewExpenseCategory, comparable: boolean): 'flat' | 'up' | 'down' {
  if (!comparable) return 'flat';
  const change = Number(row.changeAmount);
  if (change === 0) return 'flat';
  return change > 0 ? 'up' : 'down';
}

/** Estado global del bloque. El orden importa: cargando y error mandan sobre todo lo demás. */
export function overviewBlockView(input: {
  loading: boolean;
  error: boolean;
  overview: OwnerOverview | null | undefined;
}): OverviewBlockView {
  if (input.loading) return { state: 'LOADING', message: null, scopeLabel: '', scopeNotes: [] };
  if (input.error) {
    return { state: 'ERROR', message: 'No pude traer tus números ahora.', scopeLabel: '', scopeNotes: [] };
  }
  if (!input.overview) return { state: 'LOADING', message: null, scopeLabel: '', scopeNotes: [] };
  if (input.overview.scope.mode === 'NONE') {
    return { state: 'NO_ACCESS', message: input.overview.scope.reason, scopeLabel: '', scopeNotes: [] };
  }
  return {
    state: 'READY',
    message: null,
    scopeLabel: input.overview.scope.label,
    scopeNotes: scopeNotes(input.overview),
  };
}

export interface TodayView {
  amountLabel: string;
  salesLabel: string;
  /** Frase de comparación, o la línea honesta cuando todavía no hay con qué comparar. */
  comparisonText: string;
  comparisonTone: 'up' | 'down' | 'flat' | 'none';
  /** true cuando no hay ventas hoy: el bloque lo dice con palabras, no con un $0.00 a secas. */
  noSalesYet: boolean;
}

/** "Hoy llevas" — la primera de las dos únicas cifras grandes del bloque. */
export function todayView(today: OverviewToday | null): TodayView | null {
  if (!today) return null;
  const amount = Number(today.amount);
  const noSalesYet = today.saleCount === 0 && amount === 0;

  if (!today.variation || today.previousAmount === null) {
    return {
      amountLabel: money(today.amount),
      salesLabel: `${today.saleCount} ${today.saleCount === 1 ? 'venta' : 'ventas'}`,
      comparisonText: 'Todavía no tengo con qué comparar. En unas semanas te podré decir si vas mejor o peor.',
      comparisonTone: 'none',
      noSalesYet,
    };
  }

  const difference = Number(today.variation.amount);
  const weekdayText = weekdayLabel(today.previousLabel);
  const previous = money(today.previousAmount);
  const percentage = today.variation.percentage;
  const magnitude = percentage === null ? money(Math.abs(difference)) : `${Math.abs(Math.round(percentage))} %`;

  return {
    amountLabel: money(today.amount),
    salesLabel: `${today.saleCount} ${today.saleCount === 1 ? 'venta' : 'ventas'}`,
    comparisonText:
      difference === 0
        ? `Igual que ${weekdayText} (${previous}).`
        : `${magnitude} ${difference > 0 ? 'más' : 'menos'} que ${weekdayText} (${previous}).`,
    comparisonTone: difference === 0 ? 'flat' : difference > 0 ? 'up' : 'down',
    noSalesYet,
  };
}

export interface MovementRow {
  label: string;
  amountLabel: string;
  /** Proporción 0–1 para la barra; siempre relativa al mayor de los dos importes. */
  fill: number;
}

export interface MovementView {
  periodLabel: string;
  inflow: MovementRow;
  outflow: MovementRow;
  resultLabel: string;
  resultAmountLabel: string;
  /** Comparación contra el periodo anterior, o la línea honesta cuando no hay historia. */
  comparisonText: string;
  comparisonTone: 'up' | 'down' | 'flat' | 'none';
  /** Nota de honestidad obligatoria (§6): esto es movimiento, no ganancia. */
  disclaimer: string;
  /** Aviso cuando el periodo no tuvo gastos registrados (estado 8). */
  noExpensesNote: string | null;
}

const DISCLAIMER = 'De lo que registraste aquí. No incluye lo que pagas por fuera.';
const NOT_PROFIT = 'Esto muestra cómo se movió tu dinero; no necesariamente es lo mismo que tu ganancia.';

export const MOVEMENT_DISCLAIMER = DISCLAIMER;
export const MOVEMENT_NOT_PROFIT = NOT_PROFIT;

/** "Entró · Salió · Te quedó" del periodo, con su comparación a la misma altura. */
export function movementView(overview: OwnerOverview): MovementView {
  const { period } = overview;
  const inflow = Number(period.current.netSalesAmount);
  const outflow = Number(period.current.confirmedPurchasesAmount) + Number(period.current.confirmedExpensesAmount);
  const scale = Math.max(inflow, outflow, 1);

  return {
    periodLabel: periodLabel(period.fromIso, period.toIso, period.isMonthToDate),
    inflow: { label: 'Entró', amountLabel: money(inflow), fill: inflow / scale },
    outflow: { label: 'Salió', amountLabel: money(outflow), fill: outflow / scale },
    resultLabel: 'Te quedó',
    resultAmountLabel: money(period.current.moneyMovementResult),
    comparisonText: movementComparisonText(overview),
    comparisonTone: movementTone(period.variation?.result ?? null),
    disclaimer: DISCLAIMER,
    noExpensesNote:
      Number(period.current.confirmedExpensesAmount) === 0
        ? 'Todavía no has registrado gastos en este periodo. "Salió" muestra solo tus compras.'
        : null,
  };
}

function movementComparisonText(overview: OwnerOverview): string {
  const { period, history } = overview;
  if (!period.variation || !period.previous) {
    return history.daysAvailable === 0
      ? 'Todavía no tengo con qué comparar. En unas semanas te podré decir si vas mejor o peor.'
      : 'Todavía no tengo con qué comparar. En unas semanas te podré decir si vas mejor o peor.';
  }
  const previous = money(period.previous.moneyMovementResult);
  const difference = Number(period.variation.result.amount);
  const suffix = period.isMonthToDate ? 'El mes pasado, a esta altura' : 'El periodo anterior';
  if (difference === 0) return `${suffix}: ${previous}. Vas parecido.`;
  return `${suffix}: ${previous}. Son ${money(Math.abs(difference))} ${difference > 0 ? 'más' : 'menos'}.`;
}

function movementTone(variation: OverviewVariation | null): MovementView['comparisonTone'] {
  if (!variation) return 'none';
  const amount = Number(variation.amount);
  if (amount === 0) return 'flat';
  return amount > 0 ? 'up' : 'down';
}

export interface ConfidenceView {
  /** true solo cuando de verdad se puede pintar un número. */
  showValue: boolean;
  /** Encabezado del renglón. */
  label: string;
  /** El número, ya formateado. null cuando no hay número que mostrar. */
  valueLabel: string | null;
  /** Frase que acompaña al número o lo sustituye. */
  supportText: string;
  /** Cobertura como proporción, al mismo peso visual que la cifra (§5, P5). */
  coverageText: string | null;
  ctaLabel: string | null;
  ctaDestination: string | null;
  /** El titular solo lo puede ser un número CONFIABLE o ESTIMADO. */
  canBeHeadline: boolean;
  tone: 'reliable' | 'estimated' | 'incomplete' | 'blocked';
}

/**
 * La puerta única para pintar una cifra financiera. Recibe el SOBRE, nunca un número suelto: si
 * alguien quisiera renderizar un resultado sin cobertura, no tendría con qué llamar a esta función.
 *
 * Criterio A3: con el estado NO CALCULABLE no se devuelve ningún número — se devuelve qué falta y
 * el atajo para arreglarlo.
 */
export function confidenceView(
  envelope: ConfidenceEnvelope<{ amount: string }>,
  options: { label?: string } = {},
): ConfidenceView {
  const label = options.label ?? 'Tu resultado estimado';
  const blocker = envelope.blockers[0] ?? null;
  const fraction = envelope.coverage.asFraction;
  const coverageText = fraction ? `Conozco el costo de ${fraction} que vendes.` : null;

  if (envelope.state === 'NOT_COMPUTABLE' || envelope.value === null) {
    return {
      showValue: false,
      label,
      valueLabel: null, // jamás "$0.00" ni un guion mudo
      supportText: blocker?.humanText
        ? `Todavía no puedo decirte bien cuánto estás ganando. ${blocker.humanText}`
        : 'Todavía no puedo decirte bien cuánto estás ganando.',
      coverageText,
      ctaLabel: blocker?.ctaLabel ?? null,
      ctaDestination: blocker?.ctaDestination ?? null,
      canBeHeadline: false,
      tone: 'blocked',
    };
  }

  if (envelope.state === 'INCOMPLETE') {
    return {
      showValue: true,
      label,
      valueLabel: money(envelope.value.amount),
      supportText: 'No me confiaría todavía de este número; faltan costos por registrar.',
      coverageText,
      ctaLabel: blocker?.ctaLabel ?? null,
      ctaDestination: blocker?.ctaDestination ?? null,
      // Un número incompleto NUNCA es cifra hero (§5).
      canBeHeadline: false,
      tone: 'incomplete',
    };
  }

  if (envelope.state === 'ESTIMATED') {
    return {
      showValue: true,
      label,
      valueLabel: `aproximadamente ${money(envelope.value.amount)}`,
      supportText: envelope.assumptions[0] ?? 'Es una aproximación con lo que sé hasta ahora.',
      coverageText,
      ctaLabel: blocker?.ctaLabel ?? null,
      ctaDestination: blocker?.ctaDestination ?? null,
      canBeHeadline: true,
      tone: 'estimated',
    };
  }

  return {
    showValue: true,
    label,
    valueLabel: money(envelope.value.amount),
    supportText: 'Ventas menos costo de lo vendido menos gastos. Antes de impuestos y de lo que retires.',
    coverageText,
    ctaLabel: null,
    ctaDestination: null,
    canBeHeadline: true,
    tone: 'reliable',
  };
}

export interface InsightsView {
  /** true cuando no hay nada material: "Todo tranquilo por aquí" es una respuesta legítima. */
  allQuiet: boolean;
  quietHeadline: string;
  /** La evidencia de que sí se revisó — el silencio sin evidencia se lee como que la app falla. */
  quietEvidence: string;
  moreLabel: string | null;
}

export function insightsView(overview: OwnerOverview | null | undefined): InsightsView {
  const areas = overview?.insights.reviewedAreas ?? [];
  return {
    allQuiet: !overview || overview.insights.allQuiet,
    quietHeadline: 'Todo tranquilo por aquí.',
    quietEvidence: areas.length ? `Revisé ${joinList(areas)}.` : 'Revisé lo que tengo registrado.',
    moreLabel: overview && overview.insights.moreCount > 0 ? `y ${overview.insights.moreCount} más` : null,
  };
}

/** "tus ventas, tus gastos y tu inventario" */
export function joinList(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0]!;
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** "SEPTIEMBRE, HASTA HOY" para el mes en curso; el rango textual para un periodo elegido. */
export function periodLabel(fromIso: string, toIso: string, isMonthToDate: boolean): string {
  if (!fromIso || !toIso) return '';
  const [, month] = fromIso.split('-').map(Number);
  if (isMonthToDate) return `${MONTHS[(month ?? 1) - 1]}, hasta hoy`;
  return `${dayLabel(fromIso)} a ${dayLabel(toIso)}`;
}

/** "8 de septiembre" */
export function dayLabel(iso: string): string {
  const [, month, day] = iso.split('-').map(Number);
  return `${day} de ${MONTHS[(month ?? 1) - 1]}`;
}

/** "el martes pasado" a partir de la fecha del día comparado. */
export function weekdayLabel(iso: string | null): string {
  if (!iso) return 'la semana pasada';
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1, day ?? 1));
  const weekday = new Intl.DateTimeFormat('es-MX', { weekday: 'long', timeZone: 'UTC' }).format(date);
  return `el ${weekday} pasado`;
}
